function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

class RentalOrder {
  constructor() {
    this.container = document.getElementById('rental-order-details');
    if (!this.container) return;

    const params = new URLSearchParams(window.location.search);
    this.orderId = params.get('order_number') || params.get('order_id');
    this.customerEmail = params.get('email') || this.container.dataset.customerEmail;
    this.apiUrl = 'https://wonderent.wonderchef.com/api';

    console.log('Order identifier:', this.orderId);
    console.log('Customer email:', this.customerEmail);

    this.init();
  }

  async init() {
    if (!this.orderId || !this.customerEmail) {
      this.showError('Invalid order details');
      return;
    }

    await this.loadOrderDetails();
  }

  async loadOrderDetails() {
    try {
      // Add debug logging
      console.log('Loading order details:', {
        orderId: this.orderId,
        email: this.customerEmail,
        url: `${this.apiUrl}/rentals/customer/public/order/${encodeURIComponent(this.orderId)}/?email=${encodeURIComponent(this.customerEmail)}`
      });

      const response = await fetch(
        `${this.apiUrl}/rentals/customer/public/order/${encodeURIComponent(this.orderId)}/?email=${encodeURIComponent(this.customerEmail)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Origin': window.location.origin
          }
        }
      );
      
      // Add response logging
      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch order details');
      }
      
      if (!data.rental_items || data.rental_items.length === 0) {
        throw new Error('No rental items found');
      }

      await this.renderOrderDetails(data);
    } catch (error) {
      console.error('Error loading order details:', error);
      this.showError(error.message || 'Failed to load order details');
    }
  }

  async getProductImageByVariant(variantId, rental) {
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch(`${this.apiUrl}/get-product-image/${variantId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // console.log('Product image data:', data);
        return data.image_url;
      }
      // console.error('Failed to fetch image:', response.status, response.statusText);
      return rental?.product_image || 'https://placehold.co/120x120?text=Product+Image';
    } catch (error) {
      // console.error('Error fetching product image:', error);
      return rental?.product_image || 'https://placehold.co/120x120?text=Product+Image';
    }
  }

  async renderOrderDetails(orderData) {
    // Add debug logs
    // console.log('Order Data:', orderData);
    // console.log('Invoice URL:', orderData.invoice_url);

    const rentalItemsHtml = await Promise.all(orderData.rental_items.map(async (rental) => {
      // Add order-level data to each rental item
      rental.order_number = orderData.order_number;
      rental.rental_order_id = orderData.rental_order_id;

      // Get image URL for the variant
      const variantId = rental.variant_id;
      // console.log('Variant ID:', variantId);
      
      const imageUrl = await this.getProductImageByVariant(variantId, rental);
      // console.log(`Image URL for ${rental.product_name}:`, imageUrl);

      const startDate = rental.delivery_timestamp ? new Date(rental.delivery_timestamp) : null;
      const endDate = new Date(rental.return_due_date);
      const today = new Date();
      
      const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      const elapsedDays = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
      const progress = Math.min(Math.max((elapsedDays / totalDays) * 100, 0), 100);
      const daysLeft = Math.max(Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)), 0);

      const statuses = this.getOrderStatus(rental);
      const isPurchased = statuses.some(status => status.text === 'Product Purchased');
      const isPaymentCompleted = rental.status === 'PAYMENT_COMPLETED';
      
      // Show buttons if not purchased
      const shouldShowButtons = !isPurchased;

      // Define statusHtml
      const statusHtml = `
        <div class="info-item">
          <label>Order Status:</label>
          <div class="status-badges">
            ${statuses.map(status => `
              <span class="status-badge ${status.group}" 
                    style="background-color: ${status.color}20; color: ${status.color};"
                    title="${status.text}">
                ${status.text}
              </span>
            `).join('')}
          </div>
        </div>
      `;

      // Define progressHtml
      const showProgress = startDate && !isPurchased;
      const progressHtml = showProgress ? `
        <div class="rental-progress">
          <label>Rental Progress</label>
          <div class="progress-bar-container">
            <div class="progress-bar">
              <div class="progress" style="width: ${progress}%; display: block;"></div>
            </div>
            <div class="days-remaining">${daysLeft > 0 ? `${daysLeft} days remaining` : 'Expired'}</div>
          </div>
        </div>
      ` : '';

      // Action buttons HTML
      const getActionButtons = (rental) => {
        const status = rental.status.toUpperCase();
        const isReturnRelated = status.includes('RETURN_') || 
                               rental.return_status !== 'NOT_REQUESTED' ||
                               rental.return_details?.length > 0;
        
        // If any return related status, show return status details instead of button
        if (isReturnRelated) {
          return `
            <div class="rental-actions return-status-section">
              <h4>Return Status:</h4>
              <div class="return-status-details" id="return-status-${rental.id}">
                <div class="loading">Loading return status...</div>
              </div>
            </div>
          `;
        }

        // KYC Pending Status
        if (status === 'KYC_PENDING') {
            return `
                <div class="rental-actions">
                    <h4>Next Steps:</h4>
                    <div class="action-buttons-container" style="display: flex; justify-content: center;">
                        <a href="/pages/rental-kyc-initiate?order_id=${rental.rental_order_id}" 
                           class="action-button primary">
                            Complete KYC
                        </a>
                    </div>
                </div>
            `;
        }

        // Shipped Status
        if (status === 'SHIPPED') {
            return `
                <div class="rental-actions">
                    <h4>Track Your Order:</h4>
                    <div class="shipping-details" id="shipping-details-${rental.id}">
                        <div class="loading">Loading shipping details...</div>
                    </div>
                </div>
            `;
        }

        // Show all three action buttons only for DELIVERED or EXTENSION related statuses
        if (status === 'DELIVERED' || status.includes('EXTENSION')) {
            return `
                <div class="rental-actions">
                    <h4>Available Actions:</h4>
                    <div class="action-buttons-container">
                        <a href="/pages/rental-buyback?rental_id=RNT${rental.id}&original_order_id=${this.orderId}&email=${encodeURIComponent(this.customerEmail)}" 
                           class="action-button primary">
                            Purchase Product
                        </a>
                        
                        <a href="/pages/rental-extension?order_id=${rental.id}&original_order_id=${this.orderId}&email=${encodeURIComponent(this.customerEmail)}" 
                           class="action-button secondary">
                            Extend Tenure
                        </a>
                        
                        <a href="/pages/rental-return?rental_id=RNT${rental.id}&original_order_id=${this.orderId}&email=${encodeURIComponent(this.customerEmail)}" 
                           class="action-button secondary">
                            Initiate Return
                        </a>
                    </div>
                </div>
            `;
        }

        // Default: No buttons for other statuses
        return '';
      };

      const actionButtonsHtml = getActionButtons(rental);

      // Get invoice links HTML
      const invoiceLinksHtml = this.getInvoiceLinksHtml(orderData, rental);

      // After rendering the HTML, if it's return related, fetch and inject return status
      const html = `
        <div class="rental-order">
          <div class="product-details">
            <img src="${imageUrl || rental.product_image || 'https://placehold.co/120x120?text=Product+Image'}" 
                 alt="${rental.product_name}">
            <div class="product-info">
              <h3>${rental.product_name}</h3>
            </div>
          </div>

          <div class="rental-info-grid">
            <div class="info-column">
              <div class="info-item">
                <label>Rented for:</label>
                <span>${rental.rental_period} Months</span>
              </div>
              <div class="info-item">
                <label>Total Rent:</label>
                <span>₹${rental.price}</span>
              </div>
              <div class="info-item">
                <label>Refundable Deposit:</label>
                <span>₹${rental.security_deposit}</span>
              </div>
            </div>
            <div class="info-column">
              <div class="info-item">
                <label>Subscription Started:</label>
                <span>${rental.delivery_timestamp ? new Date(rental.delivery_timestamp).toLocaleDateString('en-IN') : 'Pending Delivery'}</span>
              </div>
              <div class="info-item">
                <label>Subscription End:</label>
                <span>${new Date(rental.return_due_date).toLocaleDateString('en-IN')}</span>
              </div>
              ${statusHtml}
            </div>
          </div>

          ${invoiceLinksHtml}
          
          ${showProgress ? progressHtml : ''}
          ${actionButtonsHtml}
          
          <div id="extension-history-${rental.id}">
            <div class="loading">Loading extension history...</div>
          </div>
        </div>
      `;

      if (status.includes('RETURN_') || 
          rental.return_status !== 'NOT_REQUESTED' ||
          rental.return_details?.length > 0) {
        // Fetch return status after rendering
        setTimeout(async () => {
          const returnStatus = await this.getReturnStatusDetails(rental.id, this.customerEmail);
          if (returnStatus) {
            const statusContainer = document.getElementById(`return-status-${rental.id}`);
            if (statusContainer) {
              statusContainer.innerHTML = this.renderReturnStatusDetails(returnStatus);
            }
          }
        }, 0);
      }

      setTimeout(async () => {
        const extensionDetails = await this.getExtensionDetails(rental.id, this.customerEmail);
        // console.log('Extension details fetched:', extensionDetails);
        if (extensionDetails && extensionDetails.extensions) {
          const extensionContainer = document.getElementById(`extension-history-${rental.id}`);
          if (extensionContainer) {
            extensionContainer.innerHTML = this.renderExtensionHistory(
              extensionDetails.extensions,
              this.customerEmail
            );
          }
        }
      }, 0);

      // If status is SHIPPED, fetch and inject shipping details
      if (rental.status.toUpperCase() === 'SHIPPED') {
        setTimeout(async () => {
          const shippingDetails = await this.getShippingDetails(rental);
          const shippingContainer = document.getElementById(`shipping-details-${rental.id}`);
          
          if (shippingContainer && shippingDetails) {
            shippingContainer.innerHTML = `
              <div class="shipping-info">
                <div class="carrier-info">
                  <span class="label">Carrier:</span>
                  <span class="value">${shippingDetails.carrier || 'N/A'}</span>
                </div>
                <div class="tracking-info">
                  <span class="label">Tracking Number:</span>
                  <span class="value">${shippingDetails.tracking_number || 'N/A'}</span>
                </div>
                ${shippingDetails.tracking_url ? `
                  <div class="tracking-button">
                    <a href="${shippingDetails.tracking_url}" 
                       class="action-button primary" 
                       target="_blank">
                      Track Shipment
                    </a>
                  </div>
                ` : ''}
              </div>
            `;
          }
        }, 0);
      }

      return html;
    }));

    // Combine all rental items HTML with the order header and support info
    this.container.innerHTML = `
        <div class="order-header">
            <h2>Order ID: ${orderData.order_number}</h2>
        </div>
        
        ${rentalItemsHtml.join('')}
        
        <div class="support-info">
            <p>If you have any queries, write to <a href="mailto:wonderent@wonderchef.in">wonderent@wonderchef.in</a> to get in touch with our support team.</p>
        </div>
    `;
  }

  showError(message) {
    const errorState = document.getElementById('error-state');
    const loadingState = document.getElementById('loading-state');
    
    if (errorState) {
      errorState.style.display = 'block';
      errorState.querySelector('.error-message').textContent = message;
    }
    
    if (loadingState) {
      loadingState.style.display = 'none';
    }
  }

  getOrderStatus(rental) {
    // console.log('getOrderStatus - Input rental:', rental);

    const statusMap = {
      // Payment Related Status
      'payment_settled': { text: 'Payment Settled', color: '#28a745', group: 'payment' },
      'emandate_pending': { text: 'E-Mandate Pending', color: '#ffc107', group: 'payment' },
      'emandate_failed': { text: 'E-Mandate Failed', color: '#dc3545', group: 'payment' },
      'initiated_first_payment': { text: 'Payment Initiated', color: '#17a2b8', group: 'payment' },
      'payment_failed': { text: 'Payment Failed', color: '#dc3545', group: 'payment' },
      'payment_confirmed': { text: 'Payment Confirmed', color: '#28a745', group: 'payment' },
      
      // KYC Related Status
      'kyc_pending': { text: 'KYC Pending', color: '#ffc107', group: 'kyc' },
      'kyc_rejected': { text: 'KYC Rejected', color: '#dc3545', group: 'kyc' },
      
      // Fulfillment Related Status
      'supplier_selection': { text: 'Supplier Selection', color: '#17a2b8', group: 'fulfillment' },
      'pending_fulfillment': { text: 'Pending Fulfillment', color: '#ffc107', group: 'fulfillment' },
      'shipped': { text: 'Shipped', color: '#17a2b8', group: 'fulfillment' },
      'delivered': { text: 'Rental Ongoing', color: '#28a745', group: 'fulfillment' },
      
      // Return Related Status - Updated with more specific statuses
      'PENDING': { text: 'Return Requested', color: '#ffc107', group: 'return' },
      'RETURN_APPROVED': { text: 'Return Approved', color: '#28a745', group: 'return' },
      'PROCESSING_PICKUP': { text: 'Processing Pickup', color: '#17a2b8', group: 'return' },
      'RETURN_RECEIVED': { text: 'Return Received', color: '#28a745', group: 'return' },
      'SCHEDULE_PICKUP': { text: 'Return Scheduled', color: '#17a2b8', group: 'return' },
      'PICKED_UP': { text: 'Picked Up', color: '#28a745', group: 'return' },
      'QC_PENDING': { text: 'Return QC Pending', color: '#17a2b8', group: 'return' },
      'QC_COMPLETED': { text: 'Return QC Completed', color: '#28a745', group: 'return' },
      'REFUND_PENDING': { text: 'Return Refund Pending', color: '#ffc107', group: 'return' },
      'REFUND_PROCESSED': { text: 'Return Refund Processed', color: '#28a745', group: 'return' },
      'COMPLETED': { text: 'Return Completed', color: '#28a745', group: 'return' },
      'REJECTED': { text: 'Return Rejected', color: '#dc3545', group: 'return' },
      
      // Buyback/Purchase Related Status (expanded)
      'purchase_pending': { text: 'Purchase In Progress', color: '#ffc107', group: 'purchase' },
      'purchase_completed': { text: 'Successfully Purchased', color: '#28a745', group: 'purchase' },
      'purchase_failed': { text: 'Purchase Failed', color: '#dc3545', group: 'purchase' },
      'purchased': { text: 'Product Purchased', color: '#28a745', group: 'purchase' },
      'PURCHASED': { text: 'Product Purchased', color: '#28a745', group: 'purchase' },
      'buyback_initiated': { text: 'Buyback Initiated', color: '#17a2b8', group: 'purchase' },
      'buyback_payment_pending': { text: 'Buyback Payment Pending', color: '#ffc107', group: 'purchase' },
      'buyback_completed': { text: 'Buyback Successful', color: '#28a745', group: 'purchase' },
      
      // Extension Related Status
      'extension_pending': { text: 'Extension Pending', color: '#ffc107', group: 'extension' },
      'extension_completed': { text: 'Extended', color: '#28a745', group: 'extension' }
    };

    const statuses = [];
    
    // Check return_details first for return status
    if (rental.return_details && rental.return_details.length > 0) {
        // console.log('Return details found:', rental.return_details);
        const latestReturn = rental.return_details[rental.return_details.length - 1];
        const returnStatus = latestReturn.status;
        
        if (statusMap[returnStatus]) {
            statuses.push(statusMap[returnStatus]);
            // Return early as return status takes precedence
            return statuses;
        }
    }

    // If no return status, add the main status
    if (rental.status) {
        // console.log('Regular status detected:', rental.status);
        const statusKey = rental.status.toLowerCase();
        if (statusMap[statusKey]) {
            statuses.push(statusMap[statusKey]);
        } else {
            statuses.push({ 
                text: rental.status, 
                color: '#6c757d', 
                group: 'other' 
            });
        }
    }

    // console.log('Final statuses:', statuses);
    return statuses;
  }

  getInvoiceLinksHtml(orderData, rental) {
    let invoiceHtml = '';
    
    // Debug logs
    // console.log('Generating invoice links for:', {
    //     orderData,
    //     rental,
    //     isPurchased: rental.status === 'PURCHASED',
    //     orderNumber: rental.order_number,
    //     extensionInvoices: rental.extension_invoices
    // });

    // Rental Invoice - Use order number directly as invoice number
    if (orderData.invoice_url) {
        invoiceHtml += `
            <a href="${this.apiUrl}/invoice/public/download/${rental.order_number}?email=${encodeURIComponent(this.customerEmail)}" 
               class="download-link" 
               target="_blank"
               data-invoice-type="rental">
                <span class="download-icon">📄</span>
                Download Rental Invoice
            </a>
        `;
    }
    
    // Buyback Invoice (if purchased)
    if (rental.status === 'PURCHASED') {
        // Use WRNT format for buyback invoice
        const buybackInvoiceNumber = rental.order_number;  // Already in WRNT format
        invoiceHtml += `
            <a href="${this.apiUrl}/invoice/public/download/${buybackInvoiceNumber}?email=${encodeURIComponent(this.customerEmail)}" 
               class="download-link" 
               target="_blank"
               data-invoice-type="buyback">
                <span class="download-icon">📄</span>
                Download Buyback Invoice
            </a>
        `;
    }
    
    // Extension Invoices (if any)
    if (rental.extension_invoices && rental.extension_invoices.length > 0) {
        rental.extension_invoices.forEach(invoice => {
            // Extension invoices will use their own invoice numbers
            const invoiceUrl = `${this.apiUrl}/invoice/public/download/${invoice.invoice_number}?email=${encodeURIComponent(this.customerEmail)}`;
            invoiceHtml += `
                <a href="${invoiceUrl}" 
                   class="download-link" 
                   target="_blank">
                    <span class="download-icon">📄</span>
                    Download Extension Invoice
                </a>
            `;
        });
    }
    
    return invoiceHtml ? `
        <div class="downloads">
            ${invoiceHtml}
        </div>
    ` : '';
  }

  async downloadInvoice(url, type) {
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/pdf'
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `Failed to download ${type} invoice`);
        }

        // Handle PDF download
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${type}-invoice.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        a.remove();
    } catch (error) {
        // console.error('Invoice download error:', error);
        alert(`Failed to download invoice: ${error.message}`);
    }
  }

  async getReturnStatusDetails(rentalId, email) {
    try {
      const response = await fetch(
        `${this.apiUrl}/rentals/customer/public/return-details/${rentalId}/?email=${encodeURIComponent(email)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch return status');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error fetching return status:', error);
      return null;
    }
  }

  renderReturnStatusDetails(data) {
    return `
      <div class="return-status-content">
        <div class="status-badge ${(data.status || '').toLowerCase()}">
          ${data.status || 'PENDING'}
        </div>

        <div class="return-details">
          ${data.return_initiated_date ? `
            <div class="detail-row">
              <span class="label">Return Initiated On:</span>
              <span class="value">${data.return_initiated_date}</span>
            </div>
          ` : ''}
          
          ${data.return_status ? `
            <div class="detail-row">
              <span class="label">Return Status:</span>
              <span class="value status-${(data.return_status || '').toLowerCase()}">${data.return_status}</span>
            </div>
          ` : ''}
          
          ${data.pickup_date ? `
            <div class="detail-row">
              <span class="label">Pickup Date:</span>
              <span class="value">${data.pickup_date}</span>
            </div>
          ` : ''}
          
          ${data.refund_status ? `
            <div class="detail-row">
              <span class="label">Refund Status:</span>
              <span class="value">${data.refund_status}</span>
            </div>
          ` : ''}
          
          ${data.refund_amount ? `
            <div class="detail-row">
              <span class="label">Refund Amount:</span>
              <span class="value">₹${data.refund_amount}</span>
            </div>
          ` : ''}
        </div>

        ${data.next_steps && data.next_steps.length > 0 ? `
          <div class="next-steps">
            <h3>Next Steps:</h3>
            <ul>
              ${data.next_steps.map(step => `<li>${step}</li>`).join('')}
            </ul>
          </div>
        ` : ''}
      </div>
    `;
  }

  async getExtensionDetails(rentalId, email) {
    try {
      const response = await fetch(
        `${this.apiUrl}/rentals/customer/public/extension-detail/${rentalId}/?email=${encodeURIComponent(email)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch extension details');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error fetching extension details:', error);
      return null;
    }
  }

  renderExtensionHistory(extensions, email) {
    if (!extensions || extensions.length === 0) {
        return '';
    }

    // console.log('Rendering extension history:', extensions);

    return `
        <div class="extension-history">
            <h4>Extension History</h4>
            ${extensions.map((extension, index) => {
                // console.log('Processing extension:', extension);
                return `
                    <div class="extension-card">
                        <div class="extension-header">
                            <h3>Extension #${index + 1}</h3>
                            <span class="extension-date">Extended on: ${extension.extended_on}</span>
                        </div>
                        <div class="extension-details">
                            <div class="info-row">
                                <span>Extended for:</span>
                                <span>${extension.extension_period}</span>
                            </div>
                            <div class="info-row">
                                <span>Extension Amount:</span>
                                <span>₹${extension.amount}</span>
                            </div>
                            <div class="info-row">
                                <span>Previous End Date:</span>
                                <span>${extension.previous_end_date}</span>
                            </div>
                            <div class="info-row">
                                <span>New End Date:</span>
                                <span>${extension.new_end_date}</span>
                            </div>
                        </div>
                        ${extension.invoice_number ? `
                            <div class="downloads">
                                <a href="${this.apiUrl}/invoice/public/download/${extension.invoice_number}?email=${encodeURIComponent(email)}" 
                                   class="download-link" 
                                   target="_blank">
                                    <span class="download-icon">📄</span>
                                    Download Extension Invoice (${extension.invoice_number})
                                </a>
                            </div>
                        ` : ''}
                    </div>
                `;
            }).join('')}
        </div>
    `;
  }

  async getShippingDetails(rental) {
    try {
      const response = await fetch(`${this.apiUrl}/rentals/customer/public/shipping-details/${rental.rental_order_id}/`);
      if (!response.ok) throw new Error('Failed to fetch shipping details');
      const data = await response.json();
      // console.log('Shipping details:', data);  // Debug log
      return data;
    } catch (error) {
      // console.error('Error fetching shipping details:', error);
      return null;
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new RentalOrder();
}); 